export function EnforcementPenalties() {
  const violations = [
    {
      violation: 'Operating without Provincial Registry Number',
      penalty: '$1,000 per day',
      enforcement: 'Automatic platform de-listing',
      severity: 'critical'
    },
    {
      violation: 'Operating without city business license',
      penalty: '$1,000 per day',
      enforcement: 'License revocation',
      severity: 'critical'
    },
    {
      violation: 'False principal residence declaration',
      penalty: '$10,000 - $50,000',
      enforcement: 'License revocation + criminal charges',
      severity: 'critical'
    },
    {
      violation: 'Not displaying license numbers in listings',
      penalty: '$500 - $2,000',
      enforcement: 'Warning then daily fines',
      severity: 'high'
    },
    {
      violation: 'Missing Fire Safety Plan',
      penalty: '$1,000 - $5,000',
      enforcement: 'License suspension',
      severity: 'high'
    },
    {
      violation: 'Operating in prohibited zone',
      penalty: '$2,000 - $10,000',
      enforcement: 'Immediate shutdown',
      severity: 'critical'
    }
  ];

  const enforcementMethods = [
    {
      icon: 'ri-global-line',
      title: 'Provincial Registry API',
      description: 'Platforms sync daily with BC\'s Provincial Registry. Properties without verified registry numbers are automatically de-listed within 24 hours.'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Automated Platform Monitoring',
      description: 'Vancouver uses AI-powered systems to scan Airbnb, Vrbo, and other platforms daily, cross-referencing listings with business license databases.'
    },
    {
      icon: 'ri-user-voice-line',
      title: 'Neighbor Complaints',
      description: 'The City investigates all complaints within 48 hours. Anonymous reporting is available through 311 and online portals.'
    },
    {
      icon: 'ri-database-2-line',
      title: 'Data Cross-Referencing',
      description: 'City systems automatically compare utility usage, tax records, and booking data to verify principal residence claims.'
    }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <span className="inline-block bg-[#882629]/10 text-[#882629] px-4 py-2 rounded-full text-sm font-medium mb-4">
            2026 Enforcement
          </span>
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Aggressive Tech-Driven Enforcement
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Vancouver leads Canada in STR enforcement technology. The Provincial Registry automatically de-lists non-compliant properties, and fines start at $1,000/day for operating without proper licensing.
          </p>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-8 text-center">
            Common Violations &amp; Penalties
          </h3>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#882629] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Violation</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Penalty Range</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Enforcement Action</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold">Severity</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F0F0F0]">
                  {violations.map((item, index) => (
                    <tr key={index} className="hover:bg-[#F0F0F0]/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-[#505A5A]">{item.violation}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-[#882629]">{item.penalty}</td>
                      <td className="px-6 py-4 text-sm text-[#505A5A]/70">{item.enforcement}</td>
                      <td className="px-6 py-4 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                          item.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          item.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.severity.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-8 text-center">
            How Vancouver Enforces STR Rules in 2026
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {enforcementMethods.map((method, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all text-center"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-[#882629]/10 rounded-full mx-auto mb-4">
                  <i className={`${method.icon} text-3xl text-[#882629]`}></i>
                </div>
                <h4 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {method.title}
                </h4>
                <p className="text-sm text-[#505A5A]/70 leading-relaxed">
                  {method.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 bg-gradient-to-r from-[#882629] to-[#6E1F22] rounded-2xl p-8 lg:p-10 text-white shadow-xl">
          <div className="flex flex-col lg:flex-row items-center gap-6">
            <div className="flex-shrink-0">
              <i className="ri-shield-cross-line text-6xl"></i>
            </div>
            <div className="flex-grow text-center lg:text-left">
              <h4 className="text-2xl font-bold mb-3">
                Enforcement Style: Aggressive / Tech-Driven
              </h4>
              <p className="text-lg text-white/90 leading-relaxed">
                In 2026, platforms automatically remove listings without valid Provincial Registry Numbers. A single violation can cost $1,000/day, and license revocation means permanent removal from all platforms. We ensure you stay compliant 24/7.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
